import type { Client, Interaction } from '../types'
import type { PlannerTask, TaskAuditEvent } from '../planner/types'
import { taskAuditLabel } from '../planner/audit'

export interface DailyAction {
  clientId: string
  company: string
  taxId: string
  interaction: Interaction
}

export function localDateKey(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

export function followUpTaskId(clientId: string): string {
  return `client-followup-${clientId}`
}

export function buildFollowUpTask(client: Client): PlannerTask | null {
  const interaction = client.interactions[0]
  if (!interaction?.nextContactAt) return null

  const company = client.company.trim() || 'Без названия'
  const taxId = client.taxId?.trim()
  const outcome = interaction.outcome.trim()

  return {
    id: followUpTaskId(client.id),
    title: `Позвонить · ${company}${taxId ? ` · ИНН ${taxId}` : ''}`.slice(0, 300),
    notes: [
      `Источник: ${interaction.id}`,
      'Автоматически создано из быстрой фиксации контакта.',
      outcome ? `Результат: ${outcome}` : '',
    ]
      .filter(Boolean)
      .join('\n')
      .slice(0, 5000),
    dueAt: interaction.nextContactAt,
    priority: 'normal',
    completed: false,
    reminderMinutes: 30,
    createdAt: interaction.createdAt,
    updatedAt: interaction.createdAt,
  }
}

export function taskMatchesLatestInteraction(task: PlannerTask, client: Client): boolean {
  const desired = buildFollowUpTask(client)
  if (!desired) return false

  return (
    task.id === desired.id &&
    task.title === desired.title &&
    task.notes === desired.notes &&
    task.dueAt === desired.dueAt &&
    task.priority === desired.priority &&
    task.reminderMinutes === desired.reminderMinutes
  )
}

export function dailyActions(clients: Client[], date: Date | string): DailyAction[] {
  const key = localDateKey(date)

  return clients
    .flatMap((client) =>
      client.interactions.map((interaction) => ({
        clientId: client.id,
        company: client.company || 'Без названия',
        taxId: client.taxId ?? '',
        interaction,
      })),
    )
    .filter((item) => localDateKey(item.interaction.createdAt) === key)
    .sort(
      (left, right) =>
        new Date(right.interaction.createdAt).getTime() -
        new Date(left.interaction.createdAt).getTime(),
    )
}

export function dailyTaskEvents(
  events: TaskAuditEvent[],
  date: Date | string,
): TaskAuditEvent[] {
  const key = localDateKey(date)
  return events
    .filter((event) => localDateKey(event.occurredAt) === key)
    .sort(
      (left, right) =>
        new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
    )
}

function shortTaskDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export function taskAuditDetails(event: TaskAuditEvent): string[] {
  return event.actions.map((auditAction) => {
    switch (auditAction.type) {
      case 'created':
        return `Создана задача «${event.taskTitle}» на ${shortTaskDate(event.dueAt)}`
      case 'rescheduled':
        return `Перенесена: ${shortTaskDate(auditAction.before!)} → ${shortTaskDate(auditAction.after!)}`
      case 'renamed':
        return `Переименована: «${auditAction.before}» → «${auditAction.after}»`
      case 'notes_changed':
        return 'Комментарий изменён'
      case 'priority_changed': {
        const labels: Record<string, string> = {
          low: 'низкий',
          normal: 'обычный',
          high: 'высокий',
          critical: 'критический',
        }
        return `Приоритет: ${labels[auditAction.before ?? ''] ?? auditAction.before} → ${labels[auditAction.after ?? ''] ?? auditAction.after}`
      }
      case 'reminder_changed': {
        const reminder = (value: string | null) => {
          if (value === null) return 'без напоминания'
          if (value === '0') return 'в момент задачи'
          if (value === '1440') return 'за 1 день'
          return `за ${value} мин.`
        }
        return `Напоминание: ${reminder(auditAction.before)} → ${reminder(auditAction.after)}`
      }
      case 'completed':
        return 'Задача выполнена'
      case 'reopened':
        return 'Задача возвращена в работу'
      case 'deleted':
        return `Задача «${event.before?.title ?? event.taskTitle}» удалена`
      default:
        return taskAuditLabel(auditAction.type)
    }
  })
}

export function dailySummaryText(
  actions: DailyAction[],
  date: Date | string,
  taskEvents: TaskAuditEvent[] = [],
): string {
  const day = typeof date === 'string' ? new Date(`${date}T12:00:00`) : date
  const dateText = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(day)

  const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const nextFormatter = new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

  const lines = [
    ...actions.map((item) => {
      const identity = [item.company, item.taxId ? `ИНН ${item.taxId}` : '']
        .filter(Boolean)
        .join(' · ')
      const outcome = item.interaction.outcome.trim() || 'результат не указан'
      const next = item.interaction.nextContactAt
        ? ` Следующий контакт: ${nextFormatter.format(new Date(item.interaction.nextContactAt))}.`
        : ''
      return {
        occurredAt: item.interaction.createdAt,
        text: `${identity}: ${outcome}.${next}`,
      }
    }),
    ...taskEvents.map((event) => ({
      occurredAt: event.occurredAt,
      text: `Задача «${event.taskTitle}»: ${taskAuditDetails(event).join('; ')}`,
    })),
  ].sort(
    (left, right) =>
      new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
  )

  if (!lines.length) return `${dateText}: действий не зафиксировано.`

  return [
    `Итоги за ${dateText}`,
    ...lines.map(
      (item) =>
        `${timeFormatter.format(new Date(item.occurredAt))} — ${item.text}`,
    ),
  ].join('\n')
}
